// Deep-content extension for each program page: who it's for, biomarkers,
// journey timeline, evidence links, and FAQs (rendered by ProgramDepth.jsx).

export const PROGRAM_DEPTH = {
  "health-optimization": {
    whoFor: [
      "You feel \u201coff\u201d despite normal checkups",
      "You've never had a true whole-person baseline",
      "You want prevention driven by data, not guesswork",
      "Energy, sleep or recovery has quietly declined",
      "You're starting a longevity or performance journey",
      "You want one place where all your health data lives",
    ],
    measures: [
      "Comprehensive Blood Panel",
      "Hormone & Thyroid Screen",
      "Inflammatory Markers",
      "Sleep & Recovery Trends",
      "Body Composition",
      "Lifestyle & Stress Inputs",
    ],
    timeline: [
      { step: "01", label: "Assessment", desc: "Free guided intake — history, goals, context." },
      { step: "02", label: "Baseline Testing", desc: "Comprehensive labs + wearable connection + biometrics." },
      { step: "03", label: "Matrix Analysis", desc: "The engine synthesizes your Digital Twin and surfaces priorities." },
      { step: "04", label: "Clinician Review", desc: "Your physician reviews findings and approves the protocol." },
      { step: "05", label: "Protocol & Retest", desc: "Intervention sequence with quarterly re-baseline and adjustment." },
    ],
    evidence: [
      { label: "Wearable Data in Clinical Context", href: "/evidence/wearables-hrv-clinical" },
      { label: "Insulin Resistance & Metabolic Health", href: "/evidence/insulin-resistance" },
    ],
    faqs: [
      { q: "What does the baseline include?", a: "A comprehensive blood and biomarker panel, wearable and sleep data integration, body-composition measurement, and a structured lifestyle and stress assessment — synthesized into your Digital Twin." },
      { q: "How long before I see results?", a: "You'll have a complete picture of your biology within 2–3 weeks of testing. Measurable marker improvement typically shows at the first quarterly retest." },
      { q: "Is this medical care?", a: "Yes — Health Optimization runs under physician oversight through Integrated Wellness Medical Center, with labs ordered and protocols reviewed by licensed clinicians." },
      { q: "Do I need a wearable?", a: "No, but connecting Apple Health, Oura, WHOOP, Fitbit or Garmin enriches your Digital Twin with continuous recovery and sleep signals." },
      { q: "What happens after the first quarter?", a: "We retest, compare against your baseline, and your clinician adjusts the protocol — the Measure → Learn → Adjust loop that drives every program." },
    ],
  },

  "body-transformation": {
    whoFor: [
      "You want fat loss without wrecking performance",
      "Previous diets worked briefly, then reversed",
      "You want muscle and mobility, not just a scale number",
      "You suspect hormones or recovery are limiting change",
      "You want composition tracked precisely, not guessed",
      "You're done with crash plans and want engineering",
    ],
    measures: [
      "Body Composition Scans",
      "Hormone Panel",
      "Metabolic Markers",
      "Recovery & Sleep Data",
      "Strength Benchmarks",
      "Nutrition Adherence",
    ],
    timeline: [
      { step: "01", label: "Assessment", desc: "Free guided intake — goals, training history, constraints." },
      { step: "02", label: "Composition Baseline", desc: "Patent-pending body-composition tracking + hormone and metabolic labs." },
      { step: "03", label: "Program Build", desc: "Strength, mobility, macro and supplement protocol matched to your data." },
      { step: "04", label: "Biweekly Tracking", desc: "Composition re-scans and recovery data steer weekly adjustments." },
      { step: "05", label: "Sustain & Progress", desc: "Deload windows and phase progression engineered to keep results." },
    ],
    evidence: [
      { label: "Wearable Data in Clinical Context", href: "/evidence/wearables-hrv-clinical" },
      { label: "Insulin Resistance & Metabolic Health", href: "/evidence/insulin-resistance" },
    ],
    faqs: [
      { q: "How is this different from a trainer's plan?", a: "Your plan is built from measured biology — composition scans, hormone and metabolic labs, and recovery data — and adjusted by evidence, not intuition." },
      { q: "How often is body composition measured?", a: "Typically every two weeks, using our patent-pending tracking technology, so trends drive programming rather than scale noise." },
      { q: "Will I lose muscle while cutting fat?", a: "The program is engineered to protect lean mass: protein targets, strength programming and recovery windows are set from your data and monitored at every scan." },
      { q: "Do you prescribe medication for weight loss?", a: "Where clinically appropriate, physician-directed options can be discussed through our medical arm — but training, nutrition and recovery remain the foundation." },
      { q: "What if my progress stalls?", a: "Stalls show up in the data first — composition, recovery and adherence signals. The engine flags the pattern and your coach adjusts the plan the same week." },
    ],
  },

  "cognitive-optimization": {
    whoFor: [
      "Brain fog or afternoon focus crashes",
      "High cognitive load — founder, executive, creative",
      "Memory or recall feels slower than it used to",
      "Sleep quality is inconsistent or unrefreshing",
      "Stress load is high and recovery feels thin",
      "You want measurable cognitive gains, not hacks",
    ],
    measures: [
      "Cognitive Baseline",
      "Sleep Architecture",
      "Circadian Patterns",
      "Inflammatory Markers",
      "Nutrient & Thyroid Status",
      "HRV & Stress Load",
    ],
    timeline: [
      { step: "01", label: "Assessment", desc: "Free guided intake — cognitive complaints, sleep, stress, goals." },
      { step: "02", label: "Baseline Testing", desc: "Cognitive benchmarks, labs, sleep and HRV data collection." },
      { step: "03", label: "Root-Cause Analysis", desc: "The engine reads inflammation, sleep, blood flow and nutrient status together." },
      { step: "04", label: "Protocol Build", desc: "Sleep & circadian engineering, targeted stack where appropriate, nervous-system work." },
      { step: "05", label: "Re-Benchmark", desc: "Cognitive and physiological re-testing verifies the gains are real." },
    ],
    evidence: [
      { label: "HRV, Sleep & Wearable Data", href: "/evidence/wearables-hrv-clinical" },
      { label: "The Gut Microbiome & Systemic Health", href: "/evidence/gut-microbiome" },
    ],
    faqs: [
      { q: "Is this just a nootropics program?", a: "No. Focus and memory are downstream of sleep, inflammation, blood flow and stress physiology — we measure and fix those first. Targeted nootropics are one optional layer, used where appropriate." },
      { q: "How do you measure cognitive change?", a: "A structured cognitive baseline at the start, then re-benchmarks after protocol phases — paired with sleep, HRV and lab trends so we know why performance changed." },
      { q: "Can this help with afternoon crashes?", a: "Crashes usually trace to glucose regulation, sleep debt or circadian misalignment — all measurable. The protocol targets the actual driver your data reveals." },
      { q: "Is medication involved?", a: "This is a wellness program; it doesn't prescribe. If your data suggests a medical issue (e.g., thyroid), you're routed to our physician-led medical arm." },
      { q: "How long until focus improves?", a: "Sleep-driven gains often appear within 2–4 weeks; deeper changes in inflammation and stress physiology compound over 8–12 weeks." },
    ],
  },

  "hormone-optimization": {
    whoFor: [
      "Fatigue, low drive or mood changes without clear cause",
      "Body composition shifting despite unchanged habits",
      "Suspected low testosterone or thyroid dysfunction",
      "Perimenopause or menopause symptoms",
      "Recovery from training has noticeably slowed",
      "You want balance restored — measured, not blasted",
    ],
    measures: [
      "Total & Free Testosterone",
      "Estradiol & Progesterone",
      "Full Thyroid Axis",
      "Cortisol Rhythm",
      "SHBG & DHEA",
      "Metabolic Context",
    ],
    timeline: [
      { step: "01", label: "Assessment", desc: "Free guided intake — symptoms, history, goals." },
      { step: "02", label: "Full-Axis Testing", desc: "Comprehensive hormone, thyroid and cortisol panels drawn locally." },
      { step: "03", label: "Physician Review", desc: "Your physician interprets the Matrix synthesis and confirms candidacy." },
      { step: "04", label: "Protocol Start", desc: "Lifestyle, nutrition and — where indicated — prescribed therapy, conservatively dosed." },
      { step: "05", label: "Quarterly Titration", desc: "Labs retested quarterly; dosing titrated on measured response." },
    ],
    evidence: [
      { label: "Testosterone Therapy: Evidence & Safety", href: "/evidence/testosterone-therapy" },
      { label: "Menopausal Hormone Therapy", href: "/evidence/menopause-hormone-therapy" },
    ],
    faqs: [
      { q: "Is hormone therapy safe?", a: "For appropriate, physician-screened candidates the modern evidence is reassuring — the 2023 TRAVERSE trial found testosterone therapy non-inferior to placebo for major cardiac events. Screening, conservative dosing and quarterly monitoring are how we manage risk. See our evidence review." },
      { q: "Do you treat both men and women?", a: "Yes — protocols are built separately around male and female physiology, cycle phase and life stage." },
      { q: "Can I improve hormones without prescriptions?", a: "Often. Sleep, training, body composition and micronutrient status move hormone levels meaningfully — prescriptions are an option, not the default." },
      { q: "How fast will I feel different?", a: "Lifestyle-driven changes register within weeks; prescribed protocols are typically titrated across the first two quarters as labs and symptoms are tracked." },
      { q: "Is this available in my state?", a: "Consultations, lab orders and prescriptions run through nationwide telemedicine by Integrated Wellness Medical Center, with labs drawn near you." },
    ],
  },

  "metabolic-optimization": {
    whoFor: [
      "Energy dips and post-meal crashes",
      "Rising A1c, glucose or triglycerides",
      "Family history of diabetes or heart disease",
      "Stubborn body composition despite effort",
      "You want your metabolic age measured and tracked",
      "You're building a longevity foundation",
    ],
    measures: [
      "Fasting Insulin & HOMA-IR",
      "CGM Glucose Patterns",
      "Mitochondrial Markers",
      "Oxidative Stress",
      "Lipid Fractionation",
      "Body Composition",
    ],
    timeline: [
      { step: "01", label: "Assessment", desc: "Free guided intake — history, symptoms, goals." },
      { step: "02", label: "Metabolic Baseline", desc: "Fasting insulin, lipid fractionation, inflammatory panel + 2 weeks of CGM." },
      { step: "03", label: "Pattern Analysis", desc: "The engine maps your real-world glucose response against labs and training." },
      { step: "04", label: "Protocol Build", desc: "Nutrition timing, glycemic strategy, exercise zones — physician-reviewed." },
      { step: "05", label: "Retest & Tune", desc: "Markers re-measured at 8–16 weeks; the protocol adjusts to your response." },
    ],
    evidence: [
      { label: "Insulin Resistance & Metabolic Health", href: "/evidence/insulin-resistance" },
      { label: "Biological Age & Epigenetic Clocks", href: "/evidence/biological-age-epigenetic-clocks" },
    ],
    faqs: [
      { q: "My A1c is normal — why test further?", a: "Fasting insulin and glucose variability often shift years before A1c does. Measuring them finds dysfunction in the compensated phase, when intervention works best." },
      { q: "Do I have to wear a CGM?", a: "A 2-week CGM window is strongly recommended — it reveals your personal food and stress responses — but a labs-only baseline is possible." },
      { q: "Will I need medication?", a: "Often no. Nutrition, training, sleep and composition changes are first-line; physician-directed medication is available where clinically indicated." },
      { q: "What is metabolic age?", a: "A comparison of your metabolic markers against population norms by age — one of the outputs of your baseline, and a motivating number to track." },
      { q: "How soon do markers move?", a: "CGM patterns can shift within weeks; insulin sensitivity and lipids typically show measurable movement across 8–16 weeks of consistent protocol work." },
    ],
  },

  "performance-optimization": {
    whoFor: [
      "Executives and founders running high output",
      "Athletes chasing measurable gains",
      "Recovery no longer matches your training load",
      "Travel and stress are eroding performance",
      "You want VO2, HRV and recovery engineered",
      "You want data deciding your training, not mood",
    ],
    measures: [
      "VO2-Linked Fitness",
      "HRV & Recovery Trends",
      "Training Load",
      "Sleep Architecture",
      "Hormone & Inflammatory Panel",
      "Body Composition",
    ],
    timeline: [
      { step: "01", label: "Assessment", desc: "Free guided intake — output demands, training history, goals." },
      { step: "02", label: "Performance Baseline", desc: "Labs + wearable integration + fitness and recovery benchmarks." },
      { step: "03", label: "Load Mapping", desc: "The engine reads training load against recovery, sleep and biology." },
      { step: "04", label: "Protocol Build", desc: "Training zones, recovery windows, fueling and travel strategies." },
      { step: "05", label: "Continuous Tuning", desc: "Recovery data steers weekly adjustments; benchmarks re-tested quarterly." },
    ],
    evidence: [
      { label: "HRV, Sleep & Wearable Data", href: "/evidence/wearables-hrv-clinical" },
      { label: "Insulin Resistance & Metabolic Health", href: "/evidence/insulin-resistance" },
    ],
    faqs: [
      { q: "Why does cardiorespiratory fitness matter so much?", a: "In a 122,007-patient cohort, fitness showed one of the strongest inverse associations with long-term mortality ever measured — with no upper limit of benefit. It's the most outcome-relevant performance number you own." },
      { q: "I already have a coach — does this replace them?", a: "No. We supply the biological intelligence layer — labs, recovery, load tolerance — that makes any coach's programming smarter." },
      { q: "Which wearables do you support?", a: "Apple Health, Oura, WHOOP, Fitbit and Garmin all stream natively into your Digital Twin." },
      { q: "Can this help with travel and jet lag?", a: "Yes — circadian and recovery data let us build travel protocols that protect output across time zones." },
      { q: "Is this a medical program?", a: "Performance Optimization is a wellness program. If your labs surface a medical issue, you're routed to our physician-led medical arm." },
    ],
  },

  "longevity-optimization": {
    whoFor: [
      "You want healthspan, not just lifespan",
      "Family history you intend to out-plan",
      "You want your biological age measured across 3 pillars",
      "Prevention matters more to you than treatment",
      "You're ready for a decades-long compounding plan",
      "You want emerging science graded honestly",
    ],
    measures: [
      "Biological Age (3 Pillars)",
      "Inflammation & Glycation",
      "Hormone & Thyroid Axes",
      "Insulin Sensitivity",
      "Body Composition",
      "Recovery & Fitness Trends",
    ],
    timeline: [
      { step: "01", label: "Assessment", desc: "Free guided intake — history, priorities, family risk." },
      { step: "02", label: "Longevity Baseline", desc: "Biological age testing + metabolic, hormonal and inflammatory panels." },
      { step: "03", label: "Pillar Analysis", desc: "Epigenetic, vascular and behavioral pillars reconciled; divergences flagged." },
      { step: "04", label: "Protocol Build", desc: "Physician-reviewed plan targeting your weakest pillar first." },
      { step: "05", label: "Annual Arc", desc: "Quarterly markers, annual biological-age retest — trend over snapshot." },
    ],
    evidence: [
      { label: "Biological Age & Epigenetic Clocks", href: "/evidence/biological-age-epigenetic-clocks" },
      { label: "Insulin Resistance & Metabolic Health", href: "/evidence/insulin-resistance" },
      { label: "HRV, Sleep & Wearable Data", href: "/evidence/wearables-hrv-clinical" },
    ],
    faqs: [
      { q: "Can biological age actually be improved?", a: "Published research associates lifestyle, metabolic and cardiovascular improvements with favorable movement in aging biomarkers. Individual results vary — which is why we measure yours annually rather than promise a number." },
      { q: "What makes your biological age test different?", a: "Our patent-pending IBA 2.0 engine weights three independent pillars — 30+ methylation clocks, AI vascular age, and behavioral reserve — and flags divergence patterns a single-clock test can't see." },
      { q: "Is this evidence-based or experimental?", a: "Both, clearly separated. Established interventions form the foundation; emerging science is graded (A/B/C/Emerging) in our Evidence Library so you always know which is which." },
      { q: "At what age should I start?", a: "The biology of aging is measurable by your 30s, and earlier baselines make trends more informative. Most members start between 35 and 60." },
      { q: "Is this medical care?", a: "Yes — Longevity Optimization runs under physician oversight through Integrated Wellness Medical Center, with a non-medical coaching pathway available for lifestyle-only plans." },
    ],
  },
};
